import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import API_ENDPOINTS from "../config/api";
import Cookies from "js-cookie";

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 30,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: "tween",
      ease: "easeOut",
      duration: 0.5,
    },
  },
  exit: {
    opacity: 0,
    y: -30,
    transition: {
      duration: 0.3,
    },
  },
};

// 에러 시 진동 애니메이션
const shakeAnimation = {
  x: [0, -10, 10, -10, 10, 0],
  transition: { duration: 0.5 },
};

const { OPTIONS: COOKIE_OPTIONS } = API_ENDPOINTS.COOKIE_CONFIG;

function VerificationPage() {
  const navigate = useNavigate();
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "인증코드가 일치하지 않습니다. 다시 확인해주세요."
  );
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const inputRef = useRef(null);

  const handleCodeChange = (e) => {
    // 숫자만 허용
    const numericValue = e.target.value.replace(/[^0-9]/g, "");
    setVerificationCode(numericValue);
    if (error) setError(false);

    // 8자리 모두 입력되면 키보드 닫기
    if (numericValue.length === 8 && inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleVerify = async (e) => {
    // 버튼 클릭 시 기본 동작 방지
    e.preventDefault();
    e.stopPropagation();

    // 네비게이션 상태 설정
    setIsNavigating(true);

    // 즉시 페이지 이동 (애니메이션 없이)

    if (!verificationCode.trim()) {
      setErrorMessage("인증코드를 입력해주세요.");
      setError(true);
      setShowErrorModal(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setTimeout(() => setShowErrorModal(false), 3000);
      return;
    }

    setLoading(true);

    try {
      // 실제 API 호출
      const response = await axios.get(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim/share/check/code/${verificationCode}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // 성공 시 인증 코드를 쿠키에 저장하고 다음 페이지로 이동
      if (response.status === 200) {
        // API 응답 데이터를 USER_INFO 쿠키에 JSON 형태로 저장
        const userInfoJson = JSON.stringify(response.data);
        Cookies.set("USER_INFO", userInfoJson, COOKIE_OPTIONS);

        // 인증 코드 쿠키 저장
        Cookies.set("VERIFICATION_CODE", verificationCode, COOKIE_OPTIONS);

        // claimAgree 값에 따라 다른 페이지로 이동
        if (response.data.claimAgree === true) {
          navigate("/lawyer-agreement");
        } else {
          navigate("/lawyer-agreement");
        }
      }
    } catch (error) {
      // 실패 시 에러 메시지 표시
      if (error.response?.status === 477) {
        setErrorMessage("인증코드가 만료되었습니다. 다시 시도해주세요.");
      } else {
        setErrorMessage("인증코드가 일치하지 않습니다. 다시 확인해주세요.");
      }
      setError(true);
      setShowErrorModal(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setTimeout(() => setShowErrorModal(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="page-container"
      style={{
        overflow: "hidden",
        position: "relative",
        height: "100vh",
        width: "100%",
      }}
    >
      {showErrorModal && (
        <motion.div
          className="error-modal"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <p>{errorMessage}</p>
        </motion.div>
      )}

      <motion.div
        className="page-header"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <h1>공동소유자 본인확인</h1>
      </motion.div>

      <motion.div
        className="kakao-auth-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <motion.div
          className="verification-container"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <motion.h2
            className="verification-title"
            style={{ textAlign: "center" }}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            본인 확인이 필요합니다.
          </motion.h2>
          <motion.h3
            className="verification-subtitle"
            style={{ marginBottom: "60px", textAlign: "center" }}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            카카오톡 알림톡에 기재되어 있는
            <br />
            인증코드를 입력해주세요.
          </motion.h3>

          <motion.div
            className="verification-code-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <motion.label
              htmlFor="verificationCode"
              className="verification-label"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.7 }}
            >
              인증코드
            </motion.label>
            <motion.div
              className="verification-input-container"
              initial={{ opacity: 0, y: 15 }}
              animate={shake ? shakeAnimation : { opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                id="verificationCode"
                className={`verification-input ${error ? "error" : ""}`}
                value={verificationCode}
                onChange={handleCodeChange}
                placeholder="인증코드를 입력해주세요"
                maxLength={8}
                ref={inputRef}
              />
            </motion.div>
            {error && (
              <motion.p
                className="verification-error"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                {errorMessage}
              </motion.p>
            )}
          </motion.div>
        </motion.div>
      </motion.div>

      <div style={{ height: "50px" }}></div>

      {/* 하단 버튼 영역 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: 0.5,
          duration: 0.5,
        }}
        style={{
          position: "fixed",
          bottom: "40px",
          left: "0",
          right: "0",
          padding: "20px 24px 40px",
          zIndex: 100,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          paddingBottom: "env(safe-area-inset-bottom, 40px)",
        }}
        onAnimationComplete={() => {
          // 애니메이션 완료 후 motion.div를 일반 div로 변환
          const buttonContainer = document.querySelector(".button-container");
          if (buttonContainer) {
            buttonContainer.style.transform = "none";
            buttonContainer.style.transition = "none";
          }
        }}
        className="button-container"
      >
        {/* 확인하고 계속하기 버튼 */}
        <button
          onClick={handleVerify}
          disabled={loading}
          style={{
            width: "100%",
            height: "56px",
            backgroundColor: "#7B42F6",
            color: "#FFFFFF",
            border: "none",
            borderRadius: "12px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: loading ? "not-allowed" : "pointer",
            transition: "background-color 0.2s ease",
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTapHighlightColor: "transparent",
            outline: "none",
            pointerEvents: "auto",
            opacity: loading ? 0.6 : 1,
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = "#6A35E5";
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = "#7B42F6";
            }
          }}
          onTouchStart={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = "#6A35E5";
            }
          }}
          onTouchEnd={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = "#7B42F6";
            }
          }}
        >
          {loading ? "확인중..." : "확인하고 계속하기"}
        </button>
      </motion.div>
    </div>
  );
}

export default VerificationPage;
